import React, { useState } from 'react';
import { Box, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { MyDashboardTypes } from '../../../../types/my-dashboard.types';
import { ArcChart } from '../components/ArcChart';
import { MetricRow } from '../components/MetricRow';

const BLUE = '#206FA5';
const ORANGE = '#E65100';

interface PeopleZoneProps {
    onMetricClick: (key: MyDashboardTypes.MetricKey) => void;
}

export const PeopleZone: React.FC<PeopleZoneProps> = ({ onMetricClick }) => {
    const [mode, setMode] = useState<'active' | 'inactive'>('active');
    const summary = useSelector((s: RootState) => s.myDashboard.summary);

    if (!summary) return null;

    const isActive = mode === 'active';
    const color = isActive ? BLUE : ORANGE;
    const total = summary.totalUsers;

    const keyToday: MyDashboardTypes.MetricKey = isActive ? 'activeToday' : 'inactiveToday';
    const key7Days: MyDashboardTypes.MetricKey = isActive ? 'activeLast7Days' : 'inactiveLast7Days';
    const key30Days: MyDashboardTypes.MetricKey = isActive ? 'activeLast30Days' : 'inactiveLast30Days';

    // Inactive counts come from the backend (same filter as the drill-down)
    // so zone totals and drill-down totals can't drift — rather than deriving
    // total − active, which misses users with no mobileLocation at all.
    const heroValue = isActive ? summary.activeToday : summary.inactiveToday;
    const last7Value = isActive ? summary.activeLast7Days : summary.inactiveLast7Days;
    const last30Value = isActive ? summary.activeLast30Days : summary.inactiveLast30Days;
    const pct = total > 0 ? Math.round((heroValue / total) * 100) : 0;

    return (
        <Box sx={zoneStyle}>
            {/* Header */}
            <ZoneHeader label="People" color={color} />

            {/* Toggle */}
            <ToggleButtonGroup
                value={mode}
                exclusive
                onChange={(_, v) => v && setMode(v)}
                size="small"
                sx={{
                    mb: 2.5, width: '100%',
                    bgcolor: '#F4F6F9',
                    border: '1px solid #E8ECF0',
                    borderRadius: '8px',
                    p: '3px',
                    '& .MuiToggleButton-root': {
                        flex: 1, border: 'none', borderRadius: '6px !important',
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
                        textTransform: 'uppercase', py: '6px',
                        '&.Mui-selected': {
                            color: '#fff',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                        },
                    },
                }}
            >
                <ToggleButton value="active" sx={{ '&.Mui-selected': { bgcolor: `${BLUE} !important` } }}>
                    Active
                </ToggleButton>
                <ToggleButton value="inactive" sx={{ '&.Mui-selected': { bgcolor: `${ORANGE} !important` } }}>
                    Inactive
                </ToggleButton>
            </ToggleButtonGroup>

            {/* Hero: Arc + big number — clickable, drills into today's user list */}
            <Box
                onClick={() => onMetricClick(keyToday)}
                sx={{
                    display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5,
                    cursor: 'pointer', borderRadius: '8px',
                    transition: 'background-color 0.15s',
                    mx: -1, px: 1, py: 0.75,
                    '&:hover': { bgcolor: `${color}0F` },
                }}
            >
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                    <ArcChart value={heroValue} total={total} color={color} />
                    <Box sx={{
                        position: 'absolute', inset: 0,
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1A2B3C', lineHeight: 1 }}>
                            {pct}%
                        </Typography>
                    </Box>
                </Box>
                <Box>
                    <Typography sx={{ fontSize: 13, color: '#94A3B8', mb: 0.25 }}>
                        {isActive ? 'Active today' : 'Inactive today'}
                    </Typography>
                    <Typography sx={{ fontSize: 34, fontWeight: 800, color, letterSpacing: '-0.03em', lineHeight: 1 }}>
                        {heroValue}
                        <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: '#94A3B8', ml: 0.75 }}>
                            / {total}
                        </Typography>
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.5 }}>users</Typography>
                </Box>
            </Box>

            {/* Metric rows */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2.5 }}>
                <MetricRow
                    label="Last 7 days"
                    value={last7Value}
                    total={total}
                    color={color}
                    suffix="users"
                    onClick={() => onMetricClick(key7Days)}
                />
                <MetricRow
                    label="Last 30 days"
                    value={last30Value}
                    total={total}
                    color={color}
                    suffix="users"
                    onClick={() => onMetricClick(key30Days)}
                />
            </Box>

        </Box>
    );
};

function ZoneHeader({ label, color, tag }: { label: string; color: string; tag?: string }) {
    return (
        <Box sx={{ mb: 2.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 3, height: 16, borderRadius: 1, bgcolor: color }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {label}
                    </Typography>
                </Box>
                {tag && (
                    <Box sx={{
                        fontSize: 10, fontWeight: 700, color,
                        bgcolor: `${color}12`, border: `1px solid ${color}30`,
                        px: 1, py: 0.25, borderRadius: 10,
                    }}>
                        {tag}
                    </Box>
                )}
            </Box>
        </Box>
    );
}

const zoneStyle = {
    flex: '1 1 280px',
    minWidth: 260,
    bgcolor: '#fff',
    border: '1px solid #E8ECF0',
    borderRadius: '14px',
    p: '22px 22px 16px',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
